import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { SbIcon } from '../../design/sb-icon.js';
import { trpc } from '../../trpc-client.js';
import { useDebouncedTextFilter } from '../config-list-text-filter.js';
import './mcp-servers.css';

export type McpScanStatus = 'ok' | 'no_endpoint' | 'invalid_config' | 'scan_failed';

export interface McpServerRow {
  readonly name: string;
  readonly server_type: string;
  readonly url: string | null;
  readonly raw_config: string;
  readonly scan_status: McpScanStatus;
  readonly scan_error: string | null;
  readonly scanned_at: string | null;
  readonly updated_at: string | null;
  // fn-73.1 gateway-identity marker carried verbatim from the BFF/CP — a real,
  // typed field, surfaced as the card's "Gateway" row. Optional here because
  // this narrow row type is shared with `connectors/*`, whose fixtures predate
  // the field; the live BFF payload always supplies it, so the card is accurate.
  readonly is_gateway?: boolean;
}

type McpSortBy = 'name' | 'scan_status' | 'updated_at' | 'scanned_at' | 'server_type';
type SortDirection = 'asc' | 'desc';

export interface McpServersListInput {
  readonly limit: number;
  readonly q?: string;
  readonly sort_by: McpSortBy;
  readonly sort_dir: SortDirection;
  readonly cursor?: string;
  readonly scan_status?: McpScanStatus;
}

const PAGE_SIZES = [10, 25, 50, 100] as const;
const MCP_SORT_OPTIONS: ReadonlyArray<{ readonly value: McpSortBy; readonly label: string }> = [
  { value: 'name', label: 'Name' },
  { value: 'scan_status', label: 'Status' },
  { value: 'updated_at', label: 'Updated' },
  { value: 'scanned_at', label: 'Scanned' },
  { value: 'server_type', label: 'Type' },
];
const MCP_STATUS_OPTIONS: ReadonlyArray<{ readonly value: McpScanStatus; readonly label: string }> =
  [
    { value: 'ok', label: 'Active' },
    { value: 'no_endpoint', label: 'No endpoint' },
    { value: 'invalid_config', label: 'Invalid config' },
    { value: 'scan_failed', label: 'Scan failed' },
  ];

/**
 * Design-system `.tag` tone per scan status (design 1g). Keeps the existing
 * status vocabulary/semantics — only the styling is restyled: ok→green,
 * no_endpoint→grey, scan_failed→yellow (a recoverable warning),
 * invalid_config→danger (a hard error). Sole source of truth for both the tag
 * colour and the danger card/icon treatment below.
 */
const MCP_STATUS_TAG_CLASS: Record<McpScanStatus, string> = {
  ok: 'tag--green',
  no_endpoint: 'tag--grey',
  scan_failed: 'tag--yellow',
  invalid_config: 'mcp-card__tag--danger',
};

export function formatMcpStatus(status: McpScanStatus): string {
  switch (status) {
    case 'ok':
      return 'Active';
    case 'no_endpoint':
      return 'No endpoint';
    case 'invalid_config':
      return 'Invalid config';
    case 'scan_failed':
      return 'Scan failed';
  }
}

/**
 * Only a hard config error drives the danger chrome (red outline + red icon
 * tile). A failed scan stays a yellow warning and no_endpoint stays neutral, so
 * each card's icon/border matches its own status tag colour.
 */
function isDangerStatus(status: McpScanStatus): boolean {
  return status === 'invalid_config';
}

export function readMcpServersListInput(params: URLSearchParams): McpServersListInput {
  const rawLimit = Number(params.get('limit') ?? '10');
  const limit = PAGE_SIZES.includes(rawLimit as (typeof PAGE_SIZES)[number]) ? rawLimit : 10;
  const sortBy = params.get('sort_by');
  const sort_by = MCP_SORT_OPTIONS.some((option) => option.value === sortBy)
    ? (sortBy as McpSortBy)
    : 'name';
  const sortDir = params.get('sort_dir');
  const sort_dir: SortDirection = sortDir === 'desc' ? 'desc' : 'asc';
  const q = normalizedParam(params, 'q');
  const cursor = normalizedParam(params, 'cursor');
  const scanStatus = params.get('scan_status');
  return {
    limit,
    sort_by,
    sort_dir,
    ...(q !== undefined ? { q } : {}),
    ...(cursor !== undefined ? { cursor } : {}),
    ...(isMcpStatus(scanStatus) ? { scan_status: scanStatus } : {}),
  };
}

function normalizedParam(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key)?.trim();
  return value === undefined || value.length === 0 ? undefined : value;
}

function isMcpStatus(value: string | null): value is McpScanStatus {
  return MCP_STATUS_OPTIONS.some((option) => option.value === value);
}

function hasActiveFilters(input: McpServersListInput): boolean {
  return input.q !== undefined || input.scan_status !== undefined || input.cursor !== undefined;
}

function formatDate(value: string | null): string {
  if (value === null) return '-';
  return new Date(value).toLocaleString();
}

/**
 * The scan status as a design-system `.tag` (design 1g). Renders via the page's
 * OWN `formatMcpStatus` + `MCP_STATUS_TAG_CLASS` — NOT `StatusPill` (which knows
 * only the 8 session statuses and would collapse an `McpScanStatus` to
 * "unknown"). The `mcp-status-<status>` class is a stable test/style hook.
 */
export function McpStatusTag({
  status,
  className,
}: {
  readonly status: McpScanStatus;
  readonly className?: string;
}): React.ReactElement {
  const classes = ['tag', MCP_STATUS_TAG_CLASS[status], `mcp-status-${status}`];
  if (className !== undefined) classes.push(className);
  return <span className={classes.join(' ')}>{formatMcpStatus(status)}</span>;
}

/**
 * Per-server config/status inspection disclosure (fn-52.4 / R4). A collapsed
 * `<details>` revealing the FULL MCP server projection (scan_status,
 * server_type, endpoint/url, all timestamps, scan_error, and the raw config)
 * read from the existing list payload — there is no MCP servers `byId` endpoint.
 */
export function McpInspectSection({
  server,
}: {
  server: McpServerRow;
}): React.ReactElement {
  return (
    <details className="mcp-inspect">
      <summary className="mcp-inspect__summary">Inspect config</summary>
      <dl className="mcp-inspect__meta">
        <div>
          <dt>Status</dt>
          <dd>
            <McpStatusTag status={server.scan_status} />
          </dd>
        </div>
        <div>
          <dt>Type</dt>
          <dd>{server.server_type}</dd>
        </div>
        <div>
          <dt>Endpoint</dt>
          <dd>{server.url !== null && server.url.length > 0 ? <code>{server.url}</code> : '-'}</dd>
        </div>
        <div>
          <dt>Gateway</dt>
          <dd>{server.is_gateway === undefined ? '—' : server.is_gateway ? 'Yes' : 'No'}</dd>
        </div>
        <div>
          <dt>Scanned</dt>
          <dd>{formatDate(server.scanned_at)}</dd>
        </div>
        <div>
          <dt>Updated</dt>
          <dd>{formatDate(server.updated_at)}</dd>
        </div>
      </dl>
      {server.scan_error !== null && server.scan_error.length > 0 && (
        <p className="mcp-inspect__error" role="alert">
          {server.scan_error}
        </p>
      )}
      <span className="mcp-inspect__label">Raw config</span>
      <pre className="mcp-raw-config">{server.raw_config}</pre>
    </details>
  );
}

/**
 * A single connection-health card (design 1g). Maps ONLY real `McpServerRow`
 * fields: transport (server_type), endpoint (url), gateway flag (is_gateway),
 * and the scan timestamps. No tool-count / auth-mode / last-called / enabled
 * toggle — none of those exist in the data.
 */
export function McpServerCard({ server }: { readonly server: McpServerRow }): React.ReactElement {
  const danger = isDangerStatus(server.scan_status);
  return (
    <li className={`card mcp-card${danger ? ' mcp-card--danger' : ''}`}>
      <div className="mcp-card__head">
        <span className={`mcp-card__icon${danger ? ' mcp-card__icon--danger' : ''}`}>
          <SbIcon name="plug" size={16} />
        </span>
        <span className="mcp-card__name">{server.name}</span>
        <McpStatusTag status={server.scan_status} className="mcp-card__status" />
      </div>

      {server.scan_status === 'no_endpoint' ? (
        <p className="mcp-card__note">
          No endpoint for this env
          {server.scan_error !== null ? ` — ${server.scan_error}` : ''}
        </p>
      ) : server.scan_error !== null && server.scan_error.length > 0 ? (
        <p className="mcp-card__note mcp-card__note--danger" role="alert">
          {server.scan_error}
        </p>
      ) : null}

      <div className="mcp-card__rows">
        <div className="mcp-card__row">
          <span>Transport</span>
          <span className="tag tag--grey">{server.server_type}</span>
        </div>
        {server.url !== null && server.url.length > 0 && (
          <div className="mcp-card__row">
            <span>Endpoint</span>
            <span className="mcp-card__value mcp-card__value--mono" title={server.url}>
              {server.url}
            </span>
          </div>
        )}
        <div className="mcp-card__row">
          <span>Gateway</span>
          <span className="mcp-card__value">
            {server.is_gateway === undefined ? '—' : server.is_gateway ? 'Yes' : 'No'}
          </span>
        </div>
        <div className="mcp-card__row">
          <span>Scanned</span>
          <span className="mcp-card__value">{formatDate(server.scanned_at)}</span>
        </div>
        <div className="mcp-card__row">
          <span>Updated</span>
          <span className="mcp-card__value">{formatDate(server.updated_at)}</span>
        </div>
      </div>

      <div className="mcp-card__foot">
        <div className="mcp-card__actions">
          <Link
            className="mcp-card__link"
            to={`/agents?section=config-repo&mcp_server_name=${encodeURIComponent(server.name)}`}
          >
            View agents
            <SbIcon name="chevron-right" size={14} />
          </Link>
        </div>
        <McpInspectSection server={server} />
      </div>
    </li>
  );
}

export function McpServersListPage(): React.ReactElement {
  const utils = trpc.useUtils();
  const [searchParams, setSearchParams] = useSearchParams();
  const [previousCursors, setPreviousCursors] = useState<string[]>([]);
  const listInput = readMcpServersListInput(searchParams);
  const mcpServersQuery = trpc.mcpServers.list.useQuery(listInput, {
    placeholderData: keepPreviousData,
  });
  const scanConfigRepo = trpc.mcpServers.scanConfigRepo.useMutation();

  const servers = (mcpServersQuery.data?.data ?? []) as readonly McpServerRow[];
  const lastScanAt = mcpServersQuery.data?.last_scan_at ?? null;
  const nextCursor = mcpServersQuery.data?.next_cursor ?? null;

  const setListParam = useCallback(
    (updates: Readonly<Record<string, string | null>>, replace = true): void => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value.trim().length === 0) next.delete(key);
        else next.set(key, value);
      }
      if (!Object.prototype.hasOwnProperty.call(updates, 'cursor')) {
        next.delete('cursor');
        setPreviousCursors([]);
      }
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams],
  );

  const commitSearchFilter = useCallback(
    (value: string): void => setListParam({ q: value }),
    [setListParam],
  );
  const [searchDraft, setSearchDraft] = useDebouncedTextFilter(listInput.q, commitSearchFilter);

  const clearCursorForScan = (): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('cursor');
    setPreviousCursors([]);
    setSearchParams(next, { replace: true });
  };

  if (mcpServersQuery.isPending) {
    return (
      <div className="mcp-servers-page" aria-busy="true">
        <div className="page__header">
          <div className="page__title">MCP servers</div>
        </div>
        <p className="mcp-empty">Loading MCP servers...</p>
      </div>
    );
  }

  if (mcpServersQuery.error !== null) {
    return (
      <div className="mcp-servers-page" role="alert">
        <div className="page__header">
          <div className="page__title">MCP servers</div>
        </div>
        <p className="mcp-scan-error">
          Failed to load MCP servers: {mcpServersQuery.error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="mcp-servers-page">
      <div className="page__header">
        <div className="mcp-servers-page__head">
          <div className="page__title">MCP servers</div>
          <p className="caption">Last scanned: {formatDate(lastScanAt)}</p>
        </div>
        <div className="page__actions">
          {/* Scanning the config repo is the only real mutation — MCP servers are
              authored in the config repo, so there is no "Add server" flow. */}
          <button
            type="button"
            className="btn btn--tertiary"
            disabled={scanConfigRepo.isPending}
            onClick={() => {
              void scanConfigRepo
                .mutateAsync(undefined)
                .then(async () => {
                  clearCursorForScan();
                  await utils.mcpServers.list.invalidate();
                })
                .catch(() => {});
            }}
          >
            {scanConfigRepo.isPending ? 'Scanning...' : 'Scan config repo'}
          </button>
        </div>
      </div>

      <div className="config-list-controls" aria-label="MCP server list controls">
        <label>
          <span>Search</span>
          <input
            type="search"
            value={searchDraft}
            aria-label="Search MCP servers"
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </label>
        <label>
          <span>Status</span>
          <select
            value={listInput.scan_status ?? ''}
            aria-label="Filter MCP servers by status"
            onChange={(event) => setListParam({ scan_status: event.target.value })}
          >
            <option value="">Any status</option>
            {MCP_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Sort</span>
          <select
            value={listInput.sort_by}
            aria-label="Sort MCP servers by"
            onChange={(event) => setListParam({ sort_by: event.target.value })}
          >
            {MCP_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Direction</span>
          <select
            value={listInput.sort_dir}
            aria-label="Sort MCP servers direction"
            onChange={(event) => setListParam({ sort_dir: event.target.value })}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </label>
        <label>
          <span>Page size</span>
          <select
            value={String(listInput.limit)}
            aria-label="MCP servers page size"
            onChange={(event) => setListParam({ limit: event.target.value })}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {listInput.scan_status !== undefined && (
        <div className="config-list-filter-chips" aria-label="Active MCP server filters">
          <button
            type="button"
            className="sb-chip"
            onClick={() => setListParam({ scan_status: null })}
          >
            Status: {formatMcpStatus(listInput.scan_status)} x
          </button>
        </div>
      )}

      {servers.length === 0 ? (
        <p className="mcp-empty">
          {hasActiveFilters(listInput)
            ? 'No MCP servers match current filters.'
            : 'No MCP servers scanned yet.'}
        </p>
      ) : (
        <ul className="mcp-card-grid" aria-label="MCP servers">
          {servers.map((server) => (
            <McpServerCard key={server.name} server={server} />
          ))}
        </ul>
      )}

      <div className="config-list-pagination" aria-label="MCP servers pagination">
        <button
          type="button"
          disabled={previousCursors.length === 0}
          onClick={() => {
            const previous = previousCursors[previousCursors.length - 1] ?? '';
            setPreviousCursors((stack) => stack.slice(0, -1));
            setListParam({ cursor: previous.length > 0 ? previous : null }, false);
          }}
        >
          Previous
        </button>
        <button
          type="button"
          disabled={nextCursor === null}
          onClick={() => {
            if (nextCursor === null) return;
            setPreviousCursors((stack) => [...stack, listInput.cursor ?? '']);
            setListParam({ cursor: nextCursor }, false);
          }}
        >
          Next
        </button>
      </div>

      {scanConfigRepo.data !== undefined && (
        <p className="mcp-scan-result">
          Scan found {scanConfigRepo.data.discovered_count} servers;{' '}
          {scanConfigRepo.data.valid_count} valid, {scanConfigRepo.data.invalid_count} invalid.
        </p>
      )}
      {scanConfigRepo.error !== null && (
        <p role="alert" className="mcp-scan-error">
          Failed to scan config repo: {scanConfigRepo.error.message}
        </p>
      )}
    </div>
  );
}
